import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  ProductDetail,
  ProductListItem,
  CreateProductPayload,
  PaginatedResponse,
  TimelineItem,
  UpdateProductPayload,
} from "../types";

const PRODUCTS_KEY = ["products"] as const;

export function useProductList(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    companyId?: string;
    bypassScope?: boolean;
  } = {},
): UseQueryResult<PaginatedResponse<ProductListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PRODUCTS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<ProductListItem>>("/products", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          companyId: params.companyId,
          bypassScope: params.bypassScope ? "true" : undefined,
        },
      }),
  });
}

export function useProduct(id: string): UseQueryResult<ProductDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PRODUCTS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ product: ProductDetail }>(
        `/products/${id}`,
        { token: accessToken },
      );
      return res.product;
    },
  });
}

export function useCreateProduct() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProductPayload) =>
      apiFetch<{ product: ProductDetail }>("/products", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PRODUCTS_KEY] });
    },
  });
}

export function useUpdateProduct(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateProductPayload) =>
      apiFetch<{ product: ProductDetail }>(`/products/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PRODUCTS_KEY] });
    },
  });
}

export function useDeleteProduct() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/products/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PRODUCTS_KEY] });
    },
  });
}

export function useProductSubEntity<T>(
  productId: string,
  entity: string,
  params: Record<string, string | number | undefined> = {},
) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PRODUCTS_KEY, productId, entity, params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!productId,
    queryFn: () =>
      apiFetch<PaginatedResponse<T>>(`/products/${productId}/${entity}`, {
        token: accessToken,
        query: params as Record<string, string | number>,
      }),
  });
}

export function useProductTimeline(
  productId: string,
  params: {
    page?: number;
    limit?: number;
    types?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  } = {},
) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PRODUCTS_KEY, productId, "timeline", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!productId,
    queryFn: () =>
      apiFetch<{ data: TimelineItem[]; total: number }>(
        `/products/${productId}/timeline`,
        {
          token: accessToken,
          query: {
            page: params.page,
            limit: params.limit,
            types: params.types,
            dateFrom: params.dateFrom,
            dateTo: params.dateTo,
            search: params.search,
          },
        },
      ),
  });
}
